import { fetchAuthSession } from 'aws-amplify/auth';
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { refreshSession } from '@/utils/auth';
import { PLATFORM_ASSISTANT_NAME } from '@/utils/platformChat';

type StaffChatRequest = InternalAxiosRequestConfig & { _staffChatIdRetried?: boolean };

export const isStaffPlatformChatRequest = (
  request: Pick<AxiosRequestConfig, 'url' | 'baseURL' | 'method' | 'allowAbsoluteUrls'>,
  apiUrl: string | undefined
): boolean => {
  if (request.method?.toLowerCase() !== 'post' || !request.url) return false;
  try {
    const api = new URL(apiUrl || '/api/v1', window.location.origin);
    const apiPath = api.pathname.replace(/\/+$/, '');
    const apiBase = api.origin + (apiPath.endsWith('/api/v1') ? apiPath : `${apiPath}/api/v1`);
    const target = new URL(
      axios.getUri({ ...request, baseURL: request.baseURL ?? '' }),
      window.location.origin
    );
    return (
      !target.username &&
      !target.password &&
      target.origin === api.origin &&
      target.pathname.replace(/\/+$/, '') ===
        `${new URL(apiBase).pathname}/communications/platform-chat/send`
    );
  } catch {
    return false;
  }
};

const validIdToken = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value || value.includes('[object Object]')) return null;
  try {
    const parts = value.split('.');
    if (parts.length !== 3) return null;
    const encoded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
    return payload.token_use === 'id' &&
      typeof payload.exp === 'number' &&
      Number.isFinite(payload.exp) &&
      payload.exp * 1000 > Date.now()
      ? value
      : null;
  } catch {
    return null;
  }
};

const idTokenFromSession = (
  session: Awaited<ReturnType<typeof fetchAuthSession>>
): string | null => {
  const token = session.tokens?.idToken;
  return token ? validIdToken(token.toString()) : null;
};

const refreshedIdToken = async (): Promise<string> => {
  const session = await refreshSession().catch(() => null);
  const refreshedToken = session ? idTokenFromSession(session) : null;
  if (refreshedToken) return refreshedToken;
  const token = idTokenFromSession(await fetchAuthSession({ forceRefresh: true }));
  if (!token) throw new Error(`Sign in again to use ${PLATFORM_ASSISTANT_NAME}.`);
  return token;
};

const currentIdToken = async (): Promise<string> => {
  const session = await fetchAuthSession().catch(() => null);
  const token = session ? idTokenFromSession(session) : null;
  return token || refreshedIdToken();
};

const setStaffChatToken = (request: StaffChatRequest, token: string) => {
  request.headers.set('Authorization', `Bearer ${token}`);
  request.headers.delete('X-Session-Token');
};

export const installStaffPlatformChatAuth = (client: AxiosInstance, apiUrl: string | undefined) => {
  const requestInterceptor = client.interceptors.request.use(async (config: StaffChatRequest) => {
    if (!isStaffPlatformChatRequest(config, apiUrl)) return config;
    const existing = config.headers.get('Authorization');
    const retryToken =
      config._staffChatIdRetried && typeof existing === 'string' && existing.startsWith('Bearer ')
        ? validIdToken(existing.slice('Bearer '.length))
        : null;
    setStaffChatToken(config, retryToken || (await currentIdToken()));
    return config;
  });

  const responseInterceptor = client.interceptors.response.use(
    undefined,
    async (error: unknown) => {
      if (
        !axios.isAxiosError(error) ||
        !error.config ||
        !isStaffPlatformChatRequest(error.config, apiUrl)
      ) {
        return Promise.reject(error);
      }
      const config = error.config as StaffChatRequest;
      const detail = error.response?.data?.detail || error.response?.data?.error;
      const expired =
        typeof detail === 'string' &&
        /token.*(expired|invalid)|(expired|invalid).*token/i.test(detail);
      if (
        config._staffChatIdRetried ||
        (error.response?.status !== 401 && !(error.response?.status === 403 && expired))
      ) {
        return Promise.reject(error);
      }
      config._staffChatIdRetried = true;
      setStaffChatToken(config, await refreshedIdToken());
      return client.request(config);
    }
  );

  return () => {
    client.interceptors.request.eject(requestInterceptor);
    client.interceptors.response.eject(responseInterceptor);
  };
};
